/**
 * Diferença entre duas versões de um valor JSON (usada no online: o servidor manda só o que mudou desde a última visão que o
 * técnico recebeu, em vez do estado inteiro a cada atualização).
 *
 * Nó da diferença:
 *  - `{ r: valor }`                 troca o valor inteiro
 *  - `{ o: { chave: nó }, x?: [] }` altera chaves de um objeto (`x` = chaves removidas)
 *  - `{ a?: { índice: nó }, p?: [], t?: n }` altera um array (`p` = itens acrescentados, `t` = novo tamanho se encolheu)
 */
export type NoDiferenca = { r: unknown } | { o: Record<string, NoDiferenca>; x?: string[] } | { a?: Record<string, NoDiferenca>; p?: unknown[]; t?: number };

function objeto(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** Diferença de `a` para `b` (ambos JSON puro) ou `undefined` se forem iguais. */
export function diferenca(a: unknown, b: unknown): NoDiferenca | undefined {
  if (a === b) return undefined;
  if (Array.isArray(a) && Array.isArray(b)) {
    const comum = Math.min(a.length, b.length);
    const a2: Record<string, NoDiferenca> = {};
    let mudou = false;
    for (let i = 0; i < comum; i++) {
      const d = diferenca(a[i], b[i]);
      if (d) {
        a2[i] = d;
        mudou = true;
      }
    }
    const no: { a?: Record<string, NoDiferenca>; p?: unknown[]; t?: number } = {};
    if (mudou) no.a = a2;
    if (b.length > a.length) no.p = b.slice(a.length);
    else if (b.length < a.length) no.t = b.length;
    return mudou || no.p || no.t !== undefined ? no : undefined;
  }
  if (objeto(a) && objeto(b)) {
    const o: Record<string, NoDiferenca> = {};
    let mudou = false;
    for (const k of Object.keys(b)) {
      const d = k in a ? diferenca(a[k], b[k]) : { r: b[k] };
      if (d) {
        o[k] = d;
        mudou = true;
      }
    }
    const x = Object.keys(a).filter((k) => !(k in b));
    if (!mudou && x.length === 0) return undefined;
    return x.length ? { o, x } : { o };
  }
  return { r: b };
}

/** Aplica a diferença em `base` (altera no lugar) e devolve o resultado. */
export function aplicarDiferenca(base: unknown, no: NoDiferenca): unknown {
  if ('r' in no) return no.r;
  if ('o' in no) {
    if (!objeto(base)) throw new Error('diferença não combina com a base');
    for (const k of Object.keys(no.o)) {
      if (k === '__proto__' || k === 'constructor' || k === 'prototype') continue;
      const filho = no.o[k]!;
      base[k] = aplicarDiferenca(base[k], filho);
    }
    for (const k of no.x ?? []) delete base[k];
    return base;
  }
  if (!Array.isArray(base)) throw new Error('diferença não combina com a base');
  for (const i of Object.keys(no.a ?? {})) base[Number(i)] = aplicarDiferenca(base[Number(i)], no.a![i]!);
  if (no.t !== undefined) base.length = no.t;
  for (const item of no.p ?? []) base.push(item);
  return base;
}
